import { produce } from 'immer';

import LoadingStatus from '@libs/loading-status';

import {
  startTrialWithSurvey,
  startTrialWithSurveySuccess,
} from '@app/features/subscription/state';
import { AccountAction } from '@app/store/account/actions';
import {
  ACCOUNT_AUTHENTICATION_RESET,
  ACCOUNT_LOAD_FAILURE,
  ACCOUNT_LOAD_REQUEST,
  ACCOUNT_LOAD_SUCCESS,
  ACCOUNT_SIGN_OUT_SUCCESS,
  SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_REQUEST,
  SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_SUCCESS,
} from '@app/store/actionTypes';

export interface UserDataState {
  activatedAt: string | null;
  createdAt: string | null;
  email: string | null;
  error: Error | null;
  id: string | null;
  name: string | null;
  providers: Array<{ type: string }>;
  status: LoadingStatus;
  survey: {
    businessRegistrationNumber: string | null;
    city: string | null;
    companyCategory: string | null;
    companyName: string | null;
    companyScale: string | null;
    department: string | null;
    expectedUsage: string | null;
    expectedStartTime: string | null;
    phoneNumber: string | null;
    userType: string | null;
  };
  blockInfo: {
    blockedAt: string;
  } | null;
  identification: {
    identifiedAt: string;
    expiredAt: string | null;
    identificationType: 'MOBILE' | 'MANUAL';
    identifiedName?: string;
    phoneNumber?: string;
  } | null;
}

const initialState: UserDataState = {
  activatedAt: null,
  createdAt: null,
  email: null,
  error: null,
  id: null,
  name: null,
  providers: [],
  status: LoadingStatus.IDLE,
  survey: {
    businessRegistrationNumber: null,
    city: null,
    companyCategory: null,
    companyName: null,
    companyScale: null,
    department: null,
    phoneNumber: null,
    userType: null,
    expectedUsage: null,
    expectedStartTime: null,
  },
  blockInfo: null,
  identification: null,
};

export default function reducer(
  state: UserDataState = initialState,
  action:
    | AccountAction
    | ReturnType<typeof startTrialWithSurvey>
    | ReturnType<typeof startTrialWithSurveySuccess>
): UserDataState {
  switch (action.type) {
    case ACCOUNT_LOAD_REQUEST:
      return produce(state, (draft) => {
        draft.status = LoadingStatus.PENDING;
      });
    case ACCOUNT_LOAD_SUCCESS:
      return produce(state, (draft) => {
        draft.id = action.payload.id;
        draft.email = action.payload.profile.email;
        draft.name = action.payload.profile.name;
        draft.activatedAt = action.payload.activatedAt;
        draft.createdAt = action.payload.createdAt;
        draft.status = LoadingStatus.SUCCEEDED;
        draft.identification = action.payload.identification;
        draft.blockInfo = action.payload.blockInfo;
        draft.providers = action.payload.idProviders;
      });
    case ACCOUNT_LOAD_FAILURE:
      return produce(state, (draft) => {
        draft.status = LoadingStatus.FAILED;
        draft.error = action.payload;
      });
    case ACCOUNT_AUTHENTICATION_RESET:
    case ACCOUNT_SIGN_OUT_SUCCESS:
      return initialState;
    case SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_REQUEST:
      return produce(state, (draft) => {
        draft.status = LoadingStatus.PENDING;
        draft.survey.phoneNumber = action.payload.phoneNumber;
      });
    case SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_SUCCESS:
      return produce(state, (draft) => {
        const {
          companyCategory,
          companyName,
          companyScale,
          department,
          phoneNumber,
          userType,
          expectedUsage,
          expectedStartTime,
        } = action.payload;

        draft.survey = {
          ...draft.survey,
          companyCategory: companyCategory ?? null,
          companyName: companyName ?? null,
          companyScale: companyScale ?? null,
          department: department ?? null,
          expectedUsage: expectedUsage ?? null,
          expectedStartTime: expectedStartTime ?? null,
          phoneNumber,
          userType,
        };
        draft.status = LoadingStatus.SUCCEEDED;
      });
    default:
      return state;
  }
}
